import type { ReactNode } from "react"
import { memo } from "react"

import { useTranslation } from "react-i18next"

import type { FeatureAdoptionData, FeatureUsageStatsType, UploadStatsData } from "../types"

import { Activity } from "@/components/icons"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { EmptyState } from "@/components/ui/empty-state"
import { Skeleton } from "@/components/ui/skeleton"

import { TotalSessionsCount } from "./admin-dashboard-shared"
import { CollapsibleDashboardCard } from "./collapsible-dashboard-card"

export const ModuleUsageCard = memo(function ModuleUsageCard({
  adoption,
  featureStats,
  uploadStats,
  isLoading,
  open,
  onOpenChange,
}: {
  adoption: FeatureAdoptionData | null
  featureStats: FeatureUsageStatsType | null
  uploadStats: UploadStatsData | null
  isLoading: boolean
  /** When provided, the card renders as a controlled collapsible. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const { t } = useTranslation()

  // Render the body inside either a plain Card (default) or a controlled
  // collapsible card, so the page can gate the underlying queries on `open`.
  const wrap = (body: ReactNode) =>
    open !== undefined && onOpenChange ? (
      <CollapsibleDashboardCard
        icon={Activity}
        id="adoption-card"
        open={open}
        title={t("adminDashboard.moduleUsage.title")}
        onOpenChange={onOpenChange}
      >
        {body}
      </CollapsibleDashboardCard>
    ) : (
      <Card className="rounded-lg shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="size-5" />
            {t("adminDashboard.moduleUsage.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>{body}</CardContent>
      </Card>
    )

  if (isLoading) {
    return wrap(
      <div className="space-y-6">
        <div className="space-y-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64" />
          <div className="grid gap-2 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, idx) => (
              <Skeleton key={idx} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        </div>
        <div className="bg-muted/50 rounded-lg border p-3">
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="rounded-lg border p-3">
              <Skeleton className="mb-2 h-4 w-32" />
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-2/4" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const hasAdoptionData = Boolean(adoption && adoption.totalSessions > 0)
  const hasFeatureStats = Boolean(featureStats && featureStats.totalMessages > 0)

  if (!hasAdoptionData && !hasFeatureStats) {
    return wrap(
      <EmptyState
        description={t("adminDashboard.moduleUsage.emptyDescription")}
        icon={Activity}
        title={t("adminDashboard.moduleUsage.emptyTitle")}
      />
    )
  }

  const renderAdoptionSection = () => {
    if (!adoption) {
      return null
    }

    return (
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-muted-foreground text-sm font-medium">
              {t("adminDashboard.moduleUsage.adoptionSection")}
            </div>
            <div className="text-muted-foreground text-xs">
              {t("adminDashboard.moduleUsage.totalSessions")}
            </div>
          </div>
          <div className="text-2xl font-semibold tabular-nums">
            <TotalSessionsCount value={adoption.totalSessions} />
          </div>
        </div>

        <div>
          <div className="mb-2 text-sm font-medium">
            {t("adminDashboard.moduleUsage.sessionTypes")}
          </div>
          <div className="space-y-2">
            {adoption.sessionTypes.length > 0 ? (
              adoption.sessionTypes.map((st) => (
                <div key={st.type} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{st.type}</span>
                  <div className="flex items-center gap-2">
                    <span>{st.count}</span>
                    <span className="text-muted-foreground text-xs">
                      ({st.percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-muted-foreground text-sm">
                {t("adminDashboard.moduleUsage.noSessions")}
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border p-3">
            <div className="text-muted-foreground text-xs">
              {t("adminDashboard.moduleUsage.projectAdoption")}
            </div>
            <div className="text-lg font-semibold">{adoption.projectAdoptionRate.toFixed(1)}%</div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-muted-foreground text-xs">
              {t("adminDashboard.moduleUsage.uploadAdoption")}
            </div>
            <div className="text-lg font-semibold">{adoption.uploadAdoptionRate.toFixed(1)}%</div>
          </div>
        </div>

        {uploadStats && (
          <div className="rounded-lg border p-3">
            <div className="mb-2 text-sm font-medium">
              {t("adminDashboard.moduleUsage.uploads.title")}
            </div>
            <div className="grid gap-2 text-sm sm:grid-cols-3">
              <div>
                <div className="text-muted-foreground text-xs">
                  {t("adminDashboard.moduleUsage.uploads.totalFiles")}
                </div>
                <div className="font-medium">{uploadStats.totalFiles.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">
                  {t("adminDashboard.moduleUsage.uploads.totalSize")}
                </div>
                <div className="font-medium">{uploadStats.totalSizeMB.toFixed(2)} MB</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">
                  {t("adminDashboard.moduleUsage.uploads.sessionsWithUploads")}
                </div>
                <div className="font-medium">
                  {uploadStats.sessionsWithUploads.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    )
  }

  const renderFeatureSection = () => {
    if (!featureStats) {
      return null
    }

    if (featureStats.totalMessages === 0) {
      return (
        <div className="bg-muted/50 rounded-lg border p-3">
          <div className="text-muted-foreground text-sm">
            {t("adminDashboard.moduleUsage.features.emptyDescription")}
          </div>
        </div>
      )
    }

    const featureCards: ReactNode[] = []

    if (featureStats.filePicker && featureStats.filePicker.count > 0) {
      featureCards.push(
        <div key="filePicker" className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">
            {t("adminDashboard.moduleUsage.features.filePicker.title")}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.filePicker.messages")}
              </span>
              <span>
                {featureStats.filePicker.count} ({featureStats.filePicker.percentage.toFixed(1)}%)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.filePicker.avgFiles")}
              </span>
              <span>{featureStats.filePicker.avg_files.toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.filePicker.totalSize")}
              </span>
              <span>{featureStats.filePicker.total_size_mb.toFixed(2)} MB</span>
            </div>
          </div>
        </div>
      )
    }

    if (featureStats.webSearch && featureStats.webSearch.count > 0) {
      featureCards.push(
        <div key="webSearch" className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">
            {t("adminDashboard.moduleUsage.features.webSearch.title")}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.webSearch.messages")}
              </span>
              <span>
                {featureStats.webSearch.count} ({featureStats.webSearch.percentage.toFixed(1)}%)
              </span>
            </div>
          </div>
        </div>
      )
    }

    if (featureStats.highReasoning && featureStats.highReasoning.count > 0) {
      featureCards.push(
        <div key="highReasoning" className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">
            {t("adminDashboard.moduleUsage.features.highReasoning.title")}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.highReasoning.messages")}
              </span>
              <span>
                {featureStats.highReasoning.count} (
                {featureStats.highReasoning.percentage.toFixed(1)}%)
              </span>
            </div>
            {Object.entries(featureStats.highReasoning.effort_levels).map(([level, count]) => (
              <div key={level} className="flex justify-between text-xs">
                <span className="text-muted-foreground">{level}</span>
                <span>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )
    }

    if (featureStats.trustedSources && featureStats.trustedSources.count > 0) {
      featureCards.push(
        <div key="trustedSources" className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">
            {t("adminDashboard.moduleUsage.features.trustedSources.title")}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.trustedSources.messages")}
              </span>
              <span>
                {featureStats.trustedSources.count} (
                {featureStats.trustedSources.percentage.toFixed(1)}%)
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.trustedSources.onlyMode")}
              </span>
              <span>{featureStats.trustedSources.only_mode_count}</span>
            </div>
          </div>
        </div>
      )
    }

    if (featureStats.scholar && featureStats.scholar.count > 0) {
      featureCards.push(
        <div key="scholar" className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">
            {t("adminDashboard.moduleUsage.features.scholar.title")}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.scholar.runs")}
              </span>
              <span>{featureStats.scholar.count}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.scholar.withResearch")}
              </span>
              <span>{featureStats.scholar.with_research_count}</span>
            </div>
          </div>
        </div>
      )
    }

    if (featureStats.promptLibrary && featureStats.promptLibrary.count > 0) {
      featureCards.push(
        <div key="promptLibrary" className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">
            {t("adminDashboard.moduleUsage.features.promptLibrary.title")}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.promptLibrary.usages")}
              </span>
              <span>{featureStats.promptLibrary.count}</span>
            </div>
            {featureStats.promptLibrary.top_prompts.slice(0, 3).map((prompt) => (
              <div key={prompt.id} className="flex justify-between text-xs">
                <span className="text-muted-foreground truncate" title={prompt.title}>
                  {prompt.title}
                </span>
                <span>{prompt.count}</span>
              </div>
            ))}
          </div>
        </div>
      )
    }

    if (featureStats.flow && featureStats.flow.count > 0) {
      featureCards.push(
        <div key="flow" className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">
            {t("adminDashboard.moduleUsage.features.flow.title")}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.flow.runs")}
              </span>
              <span>{featureStats.flow.count}</span>
            </div>
            {featureStats.flow.top_workflows.slice(0, 3).map((workflow) => (
              <div key={workflow.id} className="flex justify-between text-xs">
                <span className="text-muted-foreground truncate" title={workflow.id}>
                  {workflow.id}
                </span>
                <span>{workflow.count}</span>
              </div>
            ))}
          </div>
        </div>
      )
    }

    if (featureStats.activatedLibraries && featureStats.activatedLibraries.count > 0) {
      featureCards.push(
        <div key="activatedLibraries" className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">
            {t("adminDashboard.moduleUsage.features.activatedLibraries.title")}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.activatedLibraries.sessions")}
              </span>
              <span>{featureStats.activatedLibraries.count}</span>
            </div>
            {featureStats.activatedLibraries.top_libraries.slice(0, 3).map((lib) => (
              <div key={lib.id} className="flex justify-between text-xs">
                <span className="text-muted-foreground truncate" title={lib.id}>
                  {lib.id}
                </span>
                <span>{lib.count}</span>
              </div>
            ))}
          </div>
        </div>
      )
    }

    if (featureStats.toolApprovals && featureStats.toolApprovals.count > 0) {
      featureCards.push(
        <div key="toolApprovals" className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">
            {t("adminDashboard.moduleUsage.features.toolApprovals.title", "Tool Approvals")}
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("adminDashboard.moduleUsage.features.toolApprovals.total", "Total Requests")}
              </span>
              <span>{featureStats.toolApprovals.count}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-success">
                {t("adminDashboard.moduleUsage.features.toolApprovals.approved", "Approved")}
              </span>
              <span className="text-success">{featureStats.toolApprovals.approved}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-destructive">
                {t("adminDashboard.moduleUsage.features.toolApprovals.denied", "Denied")}
              </span>
              <span className="text-destructive">{featureStats.toolApprovals.denied}</span>
            </div>
            {featureStats.toolApprovals.expired > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">
                  {t("adminDashboard.moduleUsage.features.toolApprovals.expired", "Expired")}
                </span>
                <span>{featureStats.toolApprovals.expired}</span>
              </div>
            )}
            {featureStats.toolApprovals.by_tool.slice(0, 3).map((item) => (
              <div key={item.tool} className="flex justify-between text-xs">
                <span className="text-muted-foreground truncate" title={item.tool}>
                  {item.tool.replace(/_/g, " ")}
                </span>
                <span>{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <section className="space-y-4">
        <div className="text-muted-foreground text-sm font-medium">
          {t("adminDashboard.moduleUsage.features.title")}
        </div>
        <div className="bg-muted/50 rounded-lg border p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {t("adminDashboard.moduleUsage.features.totalMessages")}
            </span>
            <span className="text-lg font-semibold">
              {featureStats.totalMessages.toLocaleString()}
            </span>
          </div>
        </div>
        {featureCards.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{featureCards}</div>
        ) : (
          <EmptyState
            description={t("adminDashboard.moduleUsage.features.emptyDescription")}
            icon={Activity}
            title={t("adminDashboard.moduleUsage.features.emptyTitle")}
          />
        )}
      </section>
    )
  }

  return wrap(
    <div className="space-y-6">
      {renderAdoptionSection()}
      {renderFeatureSection()}
    </div>
  )
})
